import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { generateCodeVerifier, generateCodeChallenge } from '../helpers/pkce';
import { ChatStateService } from './chat-state.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  token: WritableSignal<string | null> = signal(localStorage.getItem('access-token'));
  idToken: WritableSignal<string | null> = signal(localStorage.getItem('id-token'));
  refreshToken: WritableSignal<string | null> = signal(localStorage.getItem('refresh-token'));

  isAuthenticated = signal(!!localStorage.getItem('access-token'));

  private idp = environment.idp;
  private http = inject(HttpClient);

  public chatState = inject(ChatStateService);

  async login() {
    let verifier = localStorage.getItem('pkce-verifier');
    if (!verifier) {
      verifier = await generateCodeVerifier();
      localStorage.setItem('pkce-verifier', verifier);
    }
    const challenge = await generateCodeChallenge(verifier);
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.idp.clientId,
      redirect_uri: this.idp.redirectUri,
      scope: this.idp.scope,
      code_challenge_method: 'S256',
      code_challenge: challenge,
      state: crypto.randomUUID(),
    });
      window.location.href = `${this.idp.authUrl}?${params.toString()}`;
  }

  async processCallback(code: string) {
    const verifier = localStorage.getItem('pkce-verifier');
    if (!verifier) throw new Error('PKCE verifier missing');
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: this.idp.redirectUri,
      client_id: this.idp.clientId,
      code_verifier: verifier!
    });
    const resp = await fetch(this.idp.jwtUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!resp.ok) {
      throw new Error(`Token exchange failed: ${resp.status}`);
    }
    const tokenResp = await resp.json();
    this.setTokens(tokenResp.id_token, tokenResp.id_token, tokenResp.refresh_token);
    return tokenResp;
  }

  async sendRefreshToken() {
    const refreshToken = localStorage.getItem('refresh-token');
    if (!refreshToken) {
      throw new Error('no refresh token');
      console.log('No refresh token available');
    }
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.idp.clientId,
      refresh_token: refreshToken!
    });
    const resp = await fetch(this.idp.jwtUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!resp.ok) {
      throw new Error(`Token exchange failed: ${resp.status}`);
    }
    const tokenResp = await resp.json();
    this.setTokens(tokenResp.id_token, tokenResp.id_token, tokenResp.refresh_token);
    return tokenResp.id_token
  }

  setTokens(accessToken: string | null, idToken?: string | null, refreshToken?: string | null) {
    this.token.set(accessToken ?? null);
    this.idToken.set(idToken ?? null);
    this.refreshToken.set(refreshToken ?? null);
    this.isAuthenticated.set(!!accessToken);
    if (accessToken) {
      localStorage.setItem('access-token', accessToken);
      this.chatState.token.set(accessToken);
    } else {
      localStorage.removeItem('access-token');
      this.chatState.token.set(null);
    }
    if (idToken) {
      localStorage.setItem('id-token', idToken);
    } else {
      localStorage.removeItem('id-token');
    }
    if (refreshToken) {
      localStorage.setItem('refresh-token', refreshToken);
    } else {
      localStorage.removeItem('refresh-token');
    }
  }

  logout() {
    this.setTokens(null, null, null);
  }

  getAccessToken() {
    return this.token();
  }

}
